/**
 * Active-society store for RESIDENTS (multi-society, MyGate model). A person can
 * belong to several societies; the top-bar SocietySwitcher picks which one the
 * app is currently scoped to, and every API request carries it as the
 * `X-Bero-Society` header (see lib/society-header.ts, which coordinates this with
 * the platform console's acting-society so they never clobber the one header
 * slot).
 *
 * Module store (not React state) so the api-client can read it synchronously
 * per-request, with a localStorage mirror on web so the choice survives reloads.
 * Native has no mirror yet — it falls back to the server's default membership
 * (lowest-id approved) until the user picks, which is the correct empty state.
 *
 * `null` = no explicit choice; the backend resolver then defaults to the user's
 * lowest-id approved membership, so a single-society user never has to pick.
 */
import React from 'react';
import { Platform } from 'react-native';

const KEY = 'bero.active_society';

function hasLocalStorage(): boolean {
  return Platform.OS === 'web' && typeof localStorage !== 'undefined';
}

function persist(id: number | null) {
  if (!hasLocalStorage()) return;
  try {
    if (id != null) localStorage.setItem(KEY, String(id));
    else localStorage.removeItem(KEY);
  } catch {
    // storage unavailable (privacy mode) — the choice just won't survive a reload
  }
}

function rehydrate(): number | null {
  if (!hasLocalStorage()) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

let current: number | null = rehydrate();
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

export function getActiveSociety(): number | null {
  return current;
}

export function setActiveSociety(id: number | null) {
  current = id;
  persist(id);
  notify();
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot() {
  return current;
}

export function useActiveSociety(): number | null {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
